import {
  DEFAULT_NEW_WINDOW_LOADER_ID,
  DEFAULT_NEW_WINDOW_LOADER_INNER_HTML,
  DEFAULT_NEW_WINDOW_LOADING_INLINE_CSS_SCRIPT,
  DEFAULT_NEW_WINDOW_TARGET
} from './constants'

/**
 * The Singleton class defines the `getInstance` method that lets clients access
 * the unique singleton instance.
 */
export class WindowOpener {
  private static instance: WindowOpener

  private _window: Window | null = null
  private _windowTarget?: string
  private _windowCloseInterval: number | undefined

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  private constructor() {}

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the Singleton class while keeping
   * just one instance of each subclass around.
   */
  public static getInstance(): WindowOpener {
    if (!WindowOpener.instance) {
      WindowOpener.instance = new WindowOpener()
    }

    return WindowOpener.instance
  }

  public openWindow(url: string, windowTarget?: string): Window | null {
    try {
      if (!this._window || this._window.closed) {
        this._windowTarget = windowTarget || DEFAULT_NEW_WINDOW_TARGET
        this._window = window.open(url, this._windowTarget)
      }

      return this._window
    } catch (error) {
      return null
    }
  }

  public addWindowLoader(
    html: string = DEFAULT_NEW_WINDOW_LOADER_INNER_HTML,
    id: string = DEFAULT_NEW_WINDOW_LOADER_ID,
    styles: string = DEFAULT_NEW_WINDOW_LOADING_INLINE_CSS_SCRIPT
  ) {
    if (!!this._window && !this._window.closed) {
      if (styles) {
        const style = document.createElement('style')
        style.innerHTML = styles
        this._window.document.head.appendChild(style)
      }

      const loaderDiv = document.createElement('div')

      if (id) {
        loaderDiv.id = id
      }

      loaderDiv.innerHTML = html
      this._window.document.body.appendChild(loaderDiv)
    }
  }

  public addWindowCloseListener(callback: () => void, timeout = 500) {
    this._windowCloseInterval = window.setInterval(() => {
      if (!!this._window && this._window.closed) {
        callback()
        this.removeWindowCloseListener()
      }
    }, timeout)
  }

  public removeWindowCloseListener() {
    window.clearInterval(this._windowCloseInterval)
    this._windowCloseInterval = undefined
  }

  public closeWindow() {
    this._window?.close()
  }

  public clearWindow(shouldCloseWindow = true) {
    if (shouldCloseWindow) {
      this._window?.close()
    }

    this._window = null
    this._windowTarget = undefined

    this.removeWindowCloseListener()
  }

  public getWindow() {
    return this._window
  }

  public getWindowTarget() {
    return this._windowTarget
  }
}
